import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';

import Confetti from 'components/Confetti';
import Loader from 'components/Loader';
import { getDateTime } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import {
  useGetHealthReading,
  useGetHealthReadingById,
} from 'services/face-scan/hooks';
import useAuthStore from 'store/auth';
import ScanDetail from '../report/ScanDetail';
import { Button } from 'components/ui/button';
import CircularProgress from 'components/CircularProgress';
import { getCategorizedScanData } from 'lib/health-readings';
import useFaceScanStore from 'store/face-scan';

const getScoreKey = (score: number) => {
  if (score >= 0 && score <= 20) return '1_2';
  if (score >= 21 && score <= 40) return '3_4';
  if (score >= 41 && score <= 60) return '5_6';
  if (score >= 61 && score <= 80) return '7_8';
  if (score >= 81 && score <= 100) return '9_10';
  return null;
};

export default function FaceScanResultPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  const profileId = useAuthStore((store) => store.currentUserProfileId);
  const setIsVideoStreamStopped = useFaceScanStore((store) => store.setIsVideoStreamStopped);
  const isVideoStreamStopped = useFaceScanStore((store) => store.isVideoStreamStopped);

  const [wellnessIndex, setWellnessIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);

  const { data: healthReadings, isFetching } = useGetHealthReading({
    profileId,
  });
  const { data: readingData, isFetching: isReadingFetching } =
    useGetHealthReadingById({
      readingId: healthReadings?.reading_data?.length
        ? healthReadings.reading_data[0].reading_id
        : '',
      enabled: !!healthReadings?.reading_data?.length,
    });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (healthReadings?.reading_data.length) {
      const isFirstScan = healthReadings.reading_data?.length === 1;

      const wellness = healthReadings.reading_data[0]?.WELLNESS_INDEX;
      setWellnessIndex(wellness);

      if (isFirstScan && wellness >= 7) {
        setShowConfetti(true);
        toast(t('congratulations'), {
          icon: '🎉',
          duration: 2000,
        });

        timeout = setTimeout(() => setShowConfetti(false), 2000);
      }
    }

    return () => timeout && clearTimeout(timeout);
  }, [healthReadings]);

  const categorizedScanData = useMemo(
    () =>
      getCategorizedScanData({ healthReadings: readingData, selectedIndex: 0 }),
    [readingData]
  );

  const reportTime = useMemo(
    () =>
      healthReadings?.reading_data.length
        ? getDateTime(healthReadings?.reading_data[0].created_at)
        : null,
    [healthReadings]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isVideoStreamStopped) {
      timeout = setTimeout(() => {
        setIsVideoStreamStopped(true);
        navigate(0);
      }, 120000); // 2 minutes to stop the video stream
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    }
  }, [isVideoStreamStopped]);

  if (isFetching || isReadingFetching) {
    return <Loader />;
  }

  if (!healthReadings?.reading_data?.length) {
    return <Navigate to={PATH_ROUTES.FACE_SCAN} replace />;
  }

  const onBack = () => {
    navigate(redirectTo ? redirectTo : PATH_ROUTES.FACE_SCAN);
  };

  const scoreKey = getScoreKey(wellnessIndex);

  return (
    <>
      <Confetti show={showConfetti} />

      <div className='relative w-full h-full px-6 pt-2 pb-4'>
        <Button
          variant='outline'
          className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
          onClick={onBack}
        >
          <ArrowLeft className='w-4 h-4 mb-px mr-1' /> {t('go_back')}
        </Button>

        <div className='flex items-center justify-between px-4 pt-2 pb-8 mt-2 lg:pt-0 sm:pt-4 lg:pb-4 h-fit lg:h-32 sm:px-10 gap-x-4 bg-primary-dark'>
          <div className='text-white'>
            <h1 className='mt-2 text-lg font-semibold sm:text-2xl'>
              {t('wellness_score_heading')}: {wellnessIndex}
            </h1>
            <p className='text-sm font-semibold sm:text-base'>
              {t(`wellness_score_heading_for_${scoreKey}`)}
            </p>
            <p className='mt-1 text-sm leading-none'>
              {t(`wellness_score_subheading_for_${scoreKey}`)}
            </p>
          </div>
          <div className='size-20 shrink-0'>
            <CircularProgress score={wellnessIndex || 0} />
          </div>
        </div>

        <div className='-mt-4 bg-white border rounded-2xl border-primary'>
          {reportTime ? (
            <div className='flex justify-center p-2 pr-6 sm:justify-end'>
              <p className='text-sm font-medium'>
                {t('report_generated_on')}: {reportTime.date} {reportTime.time}
              </p>
            </div>
          ) : null}

          <ScanDetail
            data={categorizedScanData}
            className='h-screen lg:h-[calc(100vh-21.5rem)] -mt-6'
            containerClassName='border-none pl-4 sm:pl-6'
          />
        </div>
      </div>
    </>
  );
}
